// 阵营类型
export type Faction = 'good' | 'evil';

// 玩家角色类型
export type PlayerRole = 
  | 'merlin'          // 梅林
  | 'percival'        // 派西维尔
  | 'lancelot_good'   // 好兰斯洛特 (V71 新增)
  | 'lancelot_evil'   // 坏兰斯洛特 (V133 新增)
  | 'loyal'           // 忠臣
  | 'mordred'         // 莫德雷德
  | 'morgana'         // 莫甘娜
  | 'assassin'        // 刺客
  | 'minion'          // 爪牙
  | 'oberon';         // 奥伯伦 (V71 新增)

// 角色所属阵营
export const ROLE_FACTION: Record<PlayerRole, Faction> = {
  merlin: 'good',
  percival: 'good',
  lancelot_good: 'good',   // V71 新增
  lancelot_evil: 'evil',   // V133 新增
  loyal: 'good',
  mordred: 'evil',
  morgana: 'evil',
  assassin: 'evil',
  minion: 'evil',
  oberon: 'evil',  // V71 新增
};

// 角色中文名称
export const ROLE_NAMES: Record<PlayerRole, string> = {
  merlin: '梅林',
  percival: '派西维尔',
  lancelot_good: '好兰斯洛特',  // V71 新增
  lancelot_evil: '坏兰斯洛特',   // V133 新增
  loyal: '忠臣',
  mordred: '莫德雷德',
  morgana: '莫甘娜',
  assassin: '刺客',
  minion: '爪牙',
  oberon: '奥伯伦',  // V71 新增
};

// 根据人数获取特殊角色配置
export const ROLE_CONFIG: Record<number, { good: PlayerRole[]; evil: PlayerRole[] }> = {
  5: { good: ['merlin', 'percival', 'loyal'], evil: ['morgana', 'assassin'] },
  6: { good: ['merlin', 'percival', 'loyal', 'loyal'], evil: ['morgana', 'assassin'] },
  7: { good: ['merlin', 'percival', 'loyal', 'loyal'], evil: ['mordred', 'morgana', 'assassin'] },
  8: { good: ['merlin', 'percival', 'loyal', 'loyal', 'loyal'], evil: ['mordred', 'morgana', 'assassin'] },
  9: { good: ['merlin', 'percival', 'loyal', 'loyal', 'loyal', 'loyal'], evil: ['mordred', 'morgana', 'assassin'] },
  10: { good: ['merlin', 'percival', 'loyal', 'loyal', 'loyal', 'loyal'], evil: ['mordred', 'morgana', 'assassin', 'minion'] },
  11: { good: ['merlin', 'percival', 'loyal', 'loyal', 'loyal', 'loyal', 'loyal'], evil: ['mordred', 'morgana', 'assassin', 'minion'] },
  12: { good: ['merlin', 'percival', 'loyal', 'loyal', 'loyal', 'loyal', 'loyal'], evil: ['mordred', 'morgana', 'assassin', 'minion'] },
  13: { good: ['merlin', 'percival', 'loyal', 'loyal', 'loyal', 'loyal', 'loyal', 'loyal'], evil: ['mordred', 'morgana', 'assassin', 'minion'] },
};

// 胜利方式
// V77: 优化文案 - 刺客刺杀失败 → 提前刺杀失败
// 正义阵营: 'mission' = 完成任务梅林隐藏, 'assassin_fail' = 提前刺杀失败
// 邪恶阵营: 'sabotage' = 破坏三次任务, 'assassin_success' = 刺杀梅林成功
export type VictoryType = 'mission' | 'assassin_fail' | 'sabotage' | 'assassin_success';

// 惩罚类型
export type PenaltyType = 'self_destruct' | 'wrong_kill' | 'bug';

// 惩罚类型中文名称
export const PENALTY_NAMES: Record<PenaltyType, string> = {
  self_destruct: '自爆',
  wrong_kill: '错杀',
  bug: '卡 BUG',
};

// 惩罚类型描述
export const PENALTY_DESCRIPTIONS: Record<PenaltyType, string> = {
  self_destruct: '好人自爆：正义阵营玩家提前揭示身份，破坏游戏体验',
  wrong_kill: '违规掀刀：邪恶阵营非刺客玩家强行揭示身份',
  bug: '游戏 BUG：错误投票、偷看身份等',
};

// 独立扣分记录
export interface PenaltyRecord {
  id?: number;
  player_nickname: string;
  penalty_type: PenaltyType;
  penalty_score: number;
  reason?: string;
  penalty_date: string;
  dojo_id?: number | null;
  season_id?: number | null;
  player_count?: number;
  created_at?: string;
  updated_at?: string;
}

// 游戏记录
export interface GameRecord {
  id?: number;
  game_date: string;
  player_count: number;
  winning_faction: Faction;
  victory_type: VictoryType;
  task_results?: (boolean | null)[];
  good_mvp_player?: number | null;
  evil_mvp_player?: number | null;
  self_destruct_player?: number | null;
  wrong_kill_player?: number | null;
  bug_player?: number | null;
}

// 玩家单局记录
export interface PlayerRoundRecord {
  id?: number;
  game_record_id?: number;
  player_number: number;
  player_nickname?: string;
  player_role?: PlayerRole;
  faction: Faction;
  score_change: number;
  is_mvp: boolean;
  is_good_mvp: boolean;
  is_evil_mvp: boolean;
  has_penalty: boolean;
  penalty_type?: PenaltyType | null;
  penalty_score: number;
}

// 玩家战绩统计
export interface PlayerStats {
  id?: number;
  player_number: number;
  player_name: string;
  total_score: number;
  total_games: number;
  wins: number;
  losses: number;
  good_wins: number;
  good_losses: number;
  evil_wins: number;
  evil_losses: number;
  mvp_count: number;
  good_mvp_count: number;
  evil_mvp_count: number;
}

// 积分计算结果
export interface ScoreCalculation {
  player_number: number;
  faction: Faction;
  base_score: number;
  mvp_bonus: number;
  penalty: number;
  total_score: number;
  is_mvp: boolean;
  is_good_mvp: boolean;
  is_evil_mvp: boolean;
}

// 排行榜条目
export interface LeaderboardEntry {
  rank: number;
  player_number: number;
  player_name: string;
  total_score: number;
  total_games: number;
  win_rate: number;
  mvp_count: number;
}
